import { Controller } from "@hotwired/stimulus"
import { renderStreamMessage } from "@hotwired/turbo"
import sortable from "html5sortable/dist/html5sortable.es"

export default class extends Controller {
  static targets = [
    "pool",
    "column",
    "columnsRow",
    // metricsForm + metricsMinutes/Salary live inside the metrics turbo_frame,
    // lazy-loaded from the server on card click. Stimulus rebinds them once the
    // frame swaps its content.
    "metricsForm",
    "metricsMinutes",
    "metricsSalary",
  ]
  static values = { rosterId: String, viewId: String, writable: Boolean }

  connect() {
    this.zones = [this.poolTarget, ...this.columnTargets].filter(Boolean)
    if (!this.writableValue) return

    this.initPlayerSortable()
    this.handlePlayerSort = (e) => this.onSort(e)
    this.zones.forEach((z) => z.addEventListener("sortupdate", this.handlePlayerSort))

    if (this.hasColumnsRowTarget) {
      sortable(this.columnsRowTarget, {
        items: "[data-roster-column-id]",
        handle: "[data-column-handle]",
        placeholderClass: "border-2 border-primary/40 bg-primary/5 rounded-lg w-72 shrink-0",
      })
      this.handleColumnSort = (e) => this.onColumnSort(e)
      this.columnsRowTarget.addEventListener("sortupdate", this.handleColumnSort)
    }
  }

  disconnect() {
    if (!this.writableValue) return

    this.zones.forEach((z) => z.removeEventListener("sortupdate", this.handlePlayerSort))
    sortable(this.zones, "destroy")
    if (this.hasColumnsRowTarget) {
      this.columnsRowTarget.removeEventListener("sortupdate", this.handleColumnSort)
      sortable(this.columnsRowTarget, "destroy")
    }
  }

  async onColumnSort() {
    const ids = Array.from(
      this.columnsRowTarget.querySelectorAll("[data-roster-column-id]")
    ).map((el) => el.dataset.rosterColumnId)

    const url = `/pro/rosters/${this.rosterIdValue}/roster_views/${this.viewIdValue}/roster_columns/reorder`
    await this.sendJson(url, "POST", { ids })
  }

  async onSort(event) {
    const item = event.detail.item
    const zone = event.detail.destination.container
    const rosterPlayerId = item.dataset.rosterPlayerId
    const viewId = zone.dataset.rosterViewId
    const columnId = zone.dataset.rosterColumnId || null
    const position = event.detail.destination.index

    const url = `/pro/rosters/${this.rosterIdValue}/roster_players/${rosterPlayerId}/move`
    await this.sendStream(url, "PATCH", {
      roster_view_id: viewId,
      roster_column_id: columnId,
      position,
    })
  }

  // Click handler on each roster player card. The full metrics form (including
  // the player's salary reference values) is rendered server-side and streamed
  // into the shared modal via a turbo_frame — no per-card data attributes.
  openMetrics(event) {
    const dialog = document.getElementById("roster-metrics-modal")
    const frame = document.getElementById("roster_metrics_form")
    if (!dialog || !frame) return

    const { metricsUrl } = event.currentTarget.dataset
    if (!metricsUrl) return

    // Reset frame so the previous player's form doesn't flash before reload.
    frame.innerHTML = ""
    frame.src = metricsUrl
    dialog.showModal()
  }

  async submitMetrics(event) {
    event.preventDefault()
    const form = this.metricsFormTarget
    const url = form.dataset.url
    if (!url) return

    const body = { roster_view_id: this.viewIdValue }
    const player = {}
    if (this.hasMetricsMinutesTarget) {
      player.minutes = this.metricsMinutesTarget.value
    }
    if (this.hasMetricsSalaryTarget) {
      player.salary = this.metricsSalaryTarget.value
    }
    body.roster_player = player

    const ok = await this.sendStream(url, "PATCH", body)
    if (ok) {
      const dialog = document.getElementById("roster-metrics-modal")
      if (dialog) dialog.close()
      if (this.writableValue) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            sortable(this.zones, "destroy")
            this.initPlayerSortable()
          })
        })
      }
    }
  }

  // Remove the player from the roster entirely (not just from a column).
  // The destroy stream drops the card and refreshes every total and count.
  async removePlayer(event) {
    event.preventDefault()
    const { url, confirm: message } = event.currentTarget.dataset
    if (!url) return
    if (message && !window.confirm(message)) return

    const ok = await this.sendStream(url, "DELETE", { roster_view_id: this.viewIdValue })
    if (!ok) return

    const dialog = document.getElementById("roster-metrics-modal")
    if (dialog) dialog.close()
    if (this.writableValue) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          sortable(this.zones, "destroy")
          this.initPlayerSortable()
        })
      })
    }
  }

  openColumnModal(event) {
    this.openModal(event, "roster-column-modal", "roster_column_form")
  }

  openViewModal(event) {
    this.openModal(event, "roster-view-modal", "roster_view_form")
  }

  openModal(event, dialogId, frameId) {
    const dialog = document.getElementById(dialogId)
    const frame = document.getElementById(frameId)
    if (!dialog || !frame) return

    const { modalUrl } = event.currentTarget.dataset
    if (!modalUrl) return

    frame.innerHTML = ""
    frame.src = modalUrl
    dialog.showModal()
  }

  filterPool(event) {
    const q = event.target.value.trim().toLowerCase()
    const cards = this.poolTarget.querySelectorAll("[data-search-name]")
    cards.forEach((el) => {
      const name = el.dataset.searchName || ""
      el.classList.toggle("hidden", q.length > 0 && !name.includes(q))
    })
  }

  initPlayerSortable() {
    sortable(this.zones, {
      connectWith: "rb-players",
      placeholderClass: "border-2 border-primary/40 bg-primary/5 rounded h-16 w-full",
    })
  }

  async sendStream(url, method, body) {
    const response = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken,
        Accept: "text/vnd.turbo-stream.html",
      },
      body: JSON.stringify(body),
    })
    if (response.ok) {
      const text = await response.text()
      if (text.length) renderStreamMessage(text)
    }
    return response.ok
  }

  async sendJson(url, method, body) {
    const response = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken,
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    })
    return response.ok
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]').content
  }
}
